import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Tab, Tabs, Button } from '@mui/material';
import AppLayout from '../../layout/app.layout';
import { viewDataById } from '../../services/apps.service';
import replaceIdUrl from '../../utils/url.util';
import ROUTE_PATH from '../../config/routes.config';
import Iconify from '../../components/common/iconify.component';
import AppSubscriptionSettings from '../../components/content/app-subscription-settings.component';
import AppUserOnboardMessaging from '../../components/content/app-user-onboard-msg.component';
import Loading from '../../components/table-elements/loading.component';
import SnackbarInfo from '../../components/common/snackbar-info.component';
import PageNotFoundView from '../errors/not-found.view';
import ConfirmPopup from '../../components/common/confirm-popup.component';
import AppBasicInfo from '../../components/content/app-basic-info.component';
import useAccess from '../../hooks/access.hook';
import AppWebsiteIAPPlans from '../../components/content/app-website-iap-plans.component';
import AppWebsiteContent from '../../components/content/app-website-content.component';
import AppWebsiteFields from '../../components/content/app-website-fields.component';
import AppAssetsTheme from '../../components/content/app-assets-theme.component';
import AppThirdPartyIntegrations from '../../components/content/app-third-party-integrations.component';
import AppMonetizationType from '../../components/content/app-monetization-type.component';

const TABS_CONFIG = [
  { value: 'basicInfo', label: 'App Details', component: AppBasicInfo },
  {
    value: 'monetizationType',
    label: 'Monetization Type',
    component: AppMonetizationType,
  },
  {
    value: 'subscription',
    label: 'Monetization Settings',
    component: AppSubscriptionSettings,
  },
  {
    value: 'websiteIAPPlans',
    label: 'Website IAP Plans',
    component: AppWebsiteIAPPlans,
  },
  {
    value: 'userOnboardMsg',
    label: 'In-App Messaging',
    component: AppUserOnboardMessaging,
  },
  {
    value: 'thirdPartyIntegrations',
    label: 'Third Party Integrations',
    component: AppThirdPartyIntegrations,
  },
  {
    value: 'websiteContent',
    label: 'Website Content',
    component: AppWebsiteContent,
  },
  {
    value: 'websiteFields',
    label: 'Website Fields',
    component: AppWebsiteFields,
  },
  { value: 'assetsTheme', label: 'Assets & Theme', component: AppAssetsTheme },
];

const AppEditView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasOperationAccess } = useAccess();
  const [tabValue, setTabValue] = useState(TABS_CONFIG[0].value);
  const [loading, setLoading] = useState(true);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [showPageNotFound, setShowPageNotFound] = useState(false);
  const [channelDetail, setChannelDetail] = useState({});
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [hasFormValueChanged, setHasFormValueChanged] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const tabValueRef = useRef('');

  const formValueChangedCallback = (val) => {
    setHasFormValueChanged(val);
  };

  const handleRefreshData = () => {
    setReloadCounter(reloadCounter + 1);
  };

  const handleTabSwitchSuccess = () => {
    setTabValue(tabValueRef.current);
    setHasFormValueChanged(false);
    setShowWarning(false);
  };

  useEffect(() => {
    viewDataById(id)
      .then((res) => {
        setChannelDetail(res.data);
        setLoading(false);
      })
      .catch((err) => {
        if (err.response.status === 404) {
          setShowPageNotFound(true);
          return;
        }
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [id, reloadCounter]);

  return Number.isNaN(Number(id)) || showPageNotFound ? (
    <PageNotFoundView />
  ) : (
    <AppLayout
      pageTitle={`Edit Channel Details - ${channelDetail.title}`}
      showBack
      handleBack={() => navigate(ROUTE_PATH.CONTENT_ALL_CHANNELS)}
    >
      <Box display="flex" justifyContent="flex-end" my={1}>
        {hasOperationAccess('channel_settings', 'view') && (
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="eva:eye-fill" />}
            onClick={() => {
              const url = replaceIdUrl(ROUTE_PATH.CHANNEL_VIEW_DETAILS, id);
              navigate(url);
            }}
            disabled={loading}
          >
            Switch To View Mode
          </Button>
        )}
      </Box>
      <Box height="100%" display="flex">
        <Tabs
          value={tabValue}
          aria-label="tabs"
          orientation="vertical"
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="transparent"
          sx={{ minWidth: 150, maxWidth: 150 }}
          onChange={(_, v) => {
            if (hasFormValueChanged) {
              tabValueRef.current = v;
              setShowWarning(true);
            } else {
              setTabValue(v);
            }
          }}
        >
          {TABS_CONFIG.map((tab, index) => (
            <Tab
              key={index + 1}
              value={tab.value}
              label={tab.label}
              sx={{
                background: tabValue === tab.value && '#fff',
                borderLeft: tabValue === tab.value && '2px solid #FD0100',
                alignItems: 'flex-start',
                textAlign: 'left',
              }}
            />
          ))}
        </Tabs>

        {loading && <Loading />}

        {!loading && (
          <>
            {TABS_CONFIG.map(
              ({ value, component: Component }) =>
                tabValue === value && (
                  <Component
                    key={value}
                    channelDetail={channelDetail}
                    type={tabValue}
                    handleRefreshData={handleRefreshData}
                    formValueChangedCallback={formValueChangedCallback}
                  />
                )
            )}
          </>
        )}
      </Box>

      {showWarning && (
        <ConfirmPopup
          title="Tab Switch Warning"
          message="Do you want to switch tab without saving data? Before switching tab, save your changes otherwise you lost all your changes and can not undo this action!"
          onCancel={() => setShowWarning(false)}
          onSuccess={() => handleTabSwitchSuccess()}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </AppLayout>
  );
};

export default AppEditView;
